import * as math from 'mathjs'
import CONFIG from './calculation-config.js'

const pcbPriceCalcualtor = {
  value: {},

  /**
   * Get the price with margin added.
   *
   * @param {integer} dimension
   * @param {object} pcbSpec
   * @param {float} margin
   */
  getPrice: function (bareCost, margin) {
    return (1 + margin) * bareCost
  },

  /**
   *
   * IF TOTAL DIMENSION < 1 M2, WILL HAVE PROOFING FEE
   *
   * @param {numeric} dimension
   *  The dimension unit must be in m^2.
   */
  getProofingFee: function (dimension) {
    return dimension < 1 ? CONFIG.proofingFee : 0
  },

  /**
   * PUZZLE FEE / DIFFERENT DESIGNS
   *
   * @param {numeric} constructionFee
   * @param {numeric} differentDesigns
   */
  getDifferentDesignsFee: function (constructionFee, differentDesigns) {
    return constructionFee / 2 * (differentDesigns - 1)
  },

  /**
   * The bare cost is the cost without shipment and margin added.
   *
   * @param {math.Unit|numeric} dimension
   * @param {object} pcbSpec
   * @param {object} options
   */
  calculate: function (dimension, pcbSpec, options) {
    if (dimension instanceof math.Unit) {
      dimension = dimension.toNumber('m^2')
    }

    // IF TOTAL DIMENSION < 1 M2, WILL HAVE PROOFING FEE
    this.value['proofingFee'] = options.proofing_fee ? options.proofing_fee : this.getProofingFee(dimension)

    // PCB UNIT PRICE
    this.value['unitPrice'] = this.getUnitPrice(pcbSpec.layers, dimension)

    // PCB MATERIAL COST MULTIPLIER
    this.value['pcbMaterialCost'] = CONFIG.pcbMaterialCostConfig[pcbSpec.material_details]

    // MINIMUM TRACE/SPACE COST MULTIPLIER
    this.value['mintracespaceMultiplier'] = (4 - pcbSpec.minTraceSpace) > 0
      ? 1 + ((4 - pcbSpec.minTraceSpace) * 10 * 0.03)
      : 1

    // PCB CONSTRUCTION FEE (DEPENDENT ON THE NUMBER OF LAYERS OF THE PCB)
    this.value['constructionFee'] = CONFIG.constructionFeeConfig[pcbSpec.layers]

    // PCB SERVICE FEE
    this.value['serviceFee'] = options.service_fee ? options.service_fee : CONFIG.serviceFee

    // PUZZLE FEE / DIFFERENT DESIGNS
    this.value['differentDesignsFee'] = options.different_designs_fee
      ? options.different_designs_fee
      : this.getDifferentDesignsFee(this.value['constructionFee'], pcbSpec.different_designs)

    // TEST STAND FEE (DEPENDENT ON THE NUMBER OF LAYERS OF THE PCB AND WHETHER THE TOTAL DIMENSION OF PCB IS GREATER THAN OR EQUAL TO 3m^2)
    this.value['testStandFee'] = this.getAreaRangeValue(CONFIG.testStandFeeConfig[pcbSpec.layers], dimension)
    if (!this.value['testStandFee']) {
      this.value['testStandFee'] = 0
    }

    // IMPEDANCE TEST MULTIPLIER
    this.value['impedanceTestMultiplier'] = CONFIG.impedanceTestMultiplierConfig[options.impedance_test]

    // SUB-TOTAL PRICE
    this.value['bareCost'] = _.round((
      (dimension * (this.value['unitPrice'] + CONFIG.immersionFee) * this.value['pcbMaterialCost'] * this.value['mintracespaceMultiplier'] * this.value['impedanceTestMultiplier']) + (this.value['constructionFee'] + this.value['testStandFee'] + this.value['serviceFee'] + this.value['proofingFee'] + this.value['differentDesignsFee'])
    ), 2)

    return this.value
  },

  /**
   * Get the cost with urgency price beyond standard lead time.
   *
   * @param {numeric} layers
   * @param {Math.Unit} dimension
   * @param {numeric} leadTime
   * @param {unmeric} bareCost
   */
  getUrgencyCost: function (layers, dimension, leadTime, bareCost) {
    const urgencyFeeMultipler = this.getUrgencyFeeMultipler(layers, dimension, leadTime)
    return _.round(urgencyFeeMultipler * bareCost, 2)
  },

  /**
   * Get the shipment cost.
   *
   * @param {math.Unit} dimension
   */
  getShipmentCost: function (dimension) {
    const dimensionCM2 = dimension.toNumber('cm^2')
    return _.round((((dimensionCM2 / 1000) * 46) + 175), 2)
  },

  /**
   * STANDARD LEAD TIME OF PCB MANUFACTURE (DEPENDENT ON THE NUMBER OF LAYERS AND TOTAL DIMENSION OF PCB)
   *
   * @param {numeric} layers
   * @param {Math.Unit} dimension
   */
  getLeadTime: function (layers, dimension) {
    const configs = CONFIG.standardleadTimeConfig[layers]
    return this.getRangeConfigValue(configs, dimension.toNumber('m^2'))
  },

  /**
   * PCB UNIT PRICE PER PIECE (DEPENDENT ON THE NUMBER OF LAYERS AND TOTAL DIMENSION OF THE PCB)
   *
   * @param {numeric} layers
   * @param {numeric} dimension
   */
  getUnitPrice: function (layers, dimension) {
    const configs = CONFIG.totalDimensionConfig[layers]
    return this.getRangeConfigValue(configs, dimension)
  },

  /**
   *
   * @param {numeric} layers
   * @param {Math.Unit} dimension
   * @param {numeric} leadTime
   */
  getUrgencyFeeMultipler: function (layers, dimension, leadTime) {
    const standardLeadTime = this.getLeadTime(layers, dimension)
    const urgency = leadTime < standardLeadTime ? standardLeadTime - leadTime : 0
    return urgency ? CONFIG.urgencyConfig[urgency] : 1
  },

  /**
   *
   * @param {numeric} quantity
   * @param {numeric} width
   * @param {numeric} height
   * @param {string} unitIn
   * @param {string} unitOut
   */
  getDimension: function (quantity, width, height, unitIn, unitOut) {
    const dimension = math.unit(quantity * width * height, unitIn + '^2')
    this.value['dimension'] = unitIn !== unitOut ? dimension.to(unitOut + '^2') : dimension

    return this.value['dimension']
  },

  /**
   * Helper function.
   *
   * @param {object} configs
   * @param {*} compareTo
   */
  getRangeConfigValue: function (configs, compareTo) {
    const find = _.find(configs, function (item) {
      if (item.max) {
        return compareTo > item.min && compareTo <= item.max
      } else {
        return compareTo > item.min
      }
    })
    return find ? find.value : null
  },

  /**
   * Helper function.
   *
   * @param {object} configTSF
   * @param {*} checkValidity
   */
  getAreaRangeValue (configTSF, checkValidity) {
    const find = _.find(configTSF, function (item) {
      if (item.max) {
        return checkValidity > item.min && checkValidity <= item.max
      } else {
        return checkValidity > item.min
      }
    })

    return find ? find.value : null
  }
}

export default pcbPriceCalcualtor
